import { IconRefresh } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button } from '../../../../components/Atoms';
import { blogApi, DashboardBlogArticle } from '@xver/libs/strapi/articles';
import {
    getArticlePagination,
    getArticlePopulateFields,
    getArticleSortOrder,
} from '@xver/libs/strapi/options';
import { ApplicationState } from '../../../../store';
import { AnnouncementBox, BlogArticles, ProjectsOverview, SpendRateUsage } from './components';
import DeploymentSummary from './components/DeploymentSummary/DeploymentSummary';
import { useAnnouncementsQuery } from './hooks/useAnnouncementsQuery';
import { useOverviewQuery } from './hooks/useOverviewQuery';
import { useProjectsQuery } from '../../../../hooks/useProjectsQuery';
import { useQuotasQuery } from '../../../../hooks/useQuotasQuery';
import usePageHeading from '../../../../hooks/usePageHeading';

const GREETING_KEYS = ['hallo', 'hi', 'hey', 'howdy', 'goodToSeeYou', 'greetings'] as const;

function getRandomGreetingKey(): (typeof GREETING_KEYS)[number] {
    return GREETING_KEYS[Math.floor(Math.random() * GREETING_KEYS.length)];
}

function useBlogArticlesQuery() {
    return useQuery({
        queryKey: ['strapi.articles.dashboard'],
        queryFn: async () =>
            blogApi<DashboardBlogArticle>({
                endpoint: 'articles',
                query: {
                    pagination: getArticlePagination(3),
                    populate: getArticlePopulateFields(),
                    sort: getArticleSortOrder(),
                    publicationState: 'live',
                },
            }),
        staleTime: 1000 * 60 * 5,
    });
}

export default function DashboardPage() {
    const { t } = useTranslation('dashboard');
    const headingRef = usePageHeading(t('navigation:product.dashboard.label'));
    const user = useSelector((state: ApplicationState) => state.auth.user);
    const { data, isPending: isOverviewPending, refetch: refetchOverview } = useOverviewQuery();
    const {
        data: blogData,
        isPending: isBlogPending,
        refetch: refetchBlog,
    } = useBlogArticlesQuery();
    const {
        data: announcementsData,
        isPending: isAnnouncementsPending,
        refetch: refetchAnnouncements,
    } = useAnnouncementsQuery();
    const {
        instances: instanceQuota,
        sshKeys: sshKeyQuota,
        isPending: isQuotasPending,
        refetch: refetchQuotas,
    } = useQuotasQuery();
    const {
        data: projects,
        isPending: isProjectsPending,
        isError: isProjectsError,
        isFetching: isProjectsFetching,
        errorUpdateCount: projectsErrorCount,
        refetch: refetchProjects,
    } = useProjectsQuery();
    const [isMainPending, setIsMainPending] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const greetingKey = useMemo(() => getRandomGreetingKey(), []);

    useEffect(() => {
        if (
            !isOverviewPending &&
            !isBlogPending &&
            !isAnnouncementsPending &&
            !isQuotasPending &&
            !isProjectsPending
        ) {
            setIsMainPending(false);
        }
    }, [
        isBlogPending,
        isOverviewPending,
        isAnnouncementsPending,
        isQuotasPending,
        isProjectsPending,
    ]);

    const handleRefresh = useCallback(() => {
        setIsRefreshing(true);
        void Promise.all([
            refetchOverview(),
            refetchBlog(),
            refetchAnnouncements(),
            refetchQuotas(),
            refetchProjects(),
        ]).finally(() => setIsRefreshing(false));
    }, [refetchOverview, refetchBlog, refetchAnnouncements, refetchQuotas, refetchProjects]);

    const blogArticles = useMemo(() => blogData?.data ?? [], [blogData]);

    return (
        <div className="w-full min-h-full flex flex-col justify-start items-center space-y-14">
            <div className="w-full h-auto grid grid-cols-1 sm:grid-cols-2 content-center items-center gap-4">
                <div className="h-full flex flex-row justify-start items-center gap-2">
                    <h1 ref={headingRef} tabIndex={-1} className="text-2xl">
                        {t('greeting.withName', {
                            greeting: t(`greeting.${greetingKey}`),
                            name: user?.information.firstName
                                ? user.information.firstName
                                : user?.email.split('@')[0],
                        })}
                    </h1>
                </div>
                <div className="h-full flex flex-row justify-start sm:justify-end items-center gap-2">
                    <Button
                        size="sm"
                        isLoading={isMainPending || isRefreshing}
                        variant="secondary"
                        leftIcon={<IconRefresh stroke={1.5} />}
                        onClick={handleRefresh}
                    >
                        {t('buttons.refresh')}
                    </Button>
                </div>
            </div>
            <ProjectsOverview
                projects={projects}
                isLoading={isMainPending}
                isError={isProjectsError || (projectsErrorCount > 0 && isProjectsFetching)}
                onRetry={() => void refetchProjects()}
                isRetrying={isProjectsFetching}
            />
            <div className="w-full xl:h-140 grid grid-cols-1 xl:grid-cols-3 items-start gap-16 xl:gap-8">
                <div className="w-full h-full pr-1 flex flex-col gap-10">
                    <SpendRateUsage
                        isLoading={isMainPending}
                        rollingAverage={data?.usage.rolling_average_per_day || 0}
                        currentSpendRate={data?.usage.current_spend_rate || 0}
                        cloudQuotas={data?.deployment.total_cloud_compute || 0}
                        cloudQuotaMax={instanceQuota?.limit}
                        sshKeyQuotas={data?.usage.total_ssh_keys}
                        sshKeyQuotaMax={sshKeyQuota?.limit}
                    />
                    <DeploymentSummary
                        isLoading={isMainPending}
                        cloudComputes={data?.deployment.total_cloud_compute || 0}
                        bareMetalComputes={data?.deployment.total_bare_metal_compute || 0}
                        containerEngines={data?.deployment.total_container_engine || 0}
                        hostings={data?.deployment.total_hosting || 0}
                    />
                </div>
                <BlogArticles isLoading={isMainPending} articles={blogArticles} />
                <AnnouncementBox isLoading={isMainPending} announcements={announcementsData} />
            </div>
        </div>
    );
}
